import { useState, useEffect, Suspense } from 'react';
import dynamic from 'next/dynamic';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAppState } from '../../context/app';
import Skelet from '../Skelet';
import { declOfNum, sleep } from '../../helpers';
import css from '../../styles/modules/favorites/FavoritesList.module.scss';
import ObjectsCatalogSkeletonItem from '../objects/ObjectsCatalogSkeletonItem';

import Favorite from '../../public/images/favorite.svg';

const ObjectsCatalogItem = dynamic(() => import('../objects/ObjectsCatalogItem'), { suspense: true });

const FavoritesList = () => {
  /** STATES */
  const appState = useAppState();
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);

  /** HOOKS */
  useEffect(() => {
    if (!appState.isLoaded) return;

    const fetchData = async () => {
      setIsLoading(true);
      await sleep(300);

      try {
        const response = await axios.post(process.env.NEXT_PUBLIC_API + '/favorites', { ads: appState.favorites, length: 50 });

        if (!response.data || response.data.status !== 'ok')
          throw new Error(response.data.text);

        setTotal(response.data.data.total);
        setData(response.data.data.data);
        setIsLoading(false);
        setIsError(false);
      } catch (error) {
        setIsLoading(false);
        setIsError(true);
      }
    };
    fetchData();
  }, [appState.isLoaded]);

  /** TEMPLATES */
  if (isLoading || isError)
    return (
      <section className="block">
        <div className={['fixed', css.favorites].join(' ')}>
          <div className={css.favoritesHeader}>
            <Skelet height={33} maxWidth={200} className={css.favoritesHeaderTitle} />
          </div>
          <div className={css.favoritesContent}>
            {[...Array(3).keys()].map((value) => (
              <ObjectsCatalogSkeletonItem key={value} />
            ))}
          </div>
        </div>
      </section>
    );

  return (
    <section className="block">
      <div className="fixed">
        <div className={css.favorites}>
          {data.length > 0 && (
            <div className={css.favoritesHeader}>
              <div className={css.favoritesHeaderTitle}>
                Избранное
                <span>
                  {total} {declOfNum(total, ['объект', 'объекта', 'объектов'])}
                </span>
              </div>
            </div>
          )}
          <div className={css.favoritesContent}>
            {data.length > 0 ? (
              data.map((v, i) => (
                <Suspense fallback={<ObjectsCatalogSkeletonItem />} key={i}>
                  <ObjectsCatalogItem item={v} />
                </Suspense>
              ))
            ) : (
              <motion.div
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{
                  opacity: 1,
                  scale: 1,
                  transition: { duration: 1, type: 'spring', bounce: 0.6 }
                }}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center'
                }}
              >
                <h5 style={{ marginBottom: 8 }}>Нет объектов</h5>
                <div style={{ opacity: 0.6, textAlign: 'center', margin: '0 18px' }}>
                  Жмите{' '}
                  <Favorite />
                  {' '}
                  на странице объекта и он(и) появятся здесь.
                </div>
                <img
                  src="/images/no_results.svg"
                  alt=""
                  style={{ width: '100%', maxWidth: 400, margin: '18px 32px' }}
                />
              </motion.div>
            )}
          </div>
          {data.length > 0 && (
            <div className={css.favoritesFooter}>
              <span style={{ fontWeight: 600, marginRight: 4 }}>Внимание!</span> выбранные Вами
              объекты хранятся только на Вашем устройстве
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default FavoritesList;
